import { createSelector } from 'reselect';
import { combineReducers } from 'redux';
import { get, find, filter, size, sortBy, compose, map } from 'lodash/fp';
import { record } from 'fp-ts';
import { getProp, getStringProp } from '@mews/fp';
import {
    DateTimeUtcService,
    ActionWithMeta,
    createAction,
    createAsyncActionTypes,
    createScopedSelector,
    Selector,
    AppClockService,
} from '@mews/framework';
import { ApplicationState, WithEnterpriseId, CommanderThunkAction, AsyncAction } from '@mews-commander/types';
import { EnterpriseIntegration, EnterpriseIntegrationType, IntegrationDataType } from '@mews-commander/data-access';
import { notNil, createFetchAction } from '@mews-commander/utils';
import prefix from '../prefix';
import parentSelector from '../selector';
import {
    createEntityMetaReducer,
    createAsyncMetaStateSelectors,
    createScopedEntitiesReducer,
    FetchSuccessEntitiesPayload,
    MetaState,
    Entities,
    ScopedEntities,
} from './utils';

interface EnterpriseIntegrationState extends MetaState {
    data: ScopedEntities<EnterpriseIntegration>;
}

export const NAME = 'enterpriseIntegrations';

export const ENTERPRISE_INTEGRATIONS_FETCH = prefix('ENTERPRISE_INTEGRATIONS_FETCH');
export const [
    ENTERPRISE_INTEGRATIONS_FETCH_STARTED,
    ENTERPRISE_INTEGRATIONS_FETCH_SUCCEEDED,
    ENTERPRISE_INTEGRATIONS_FETCH_FAILED,
] = createAsyncActionTypes(ENTERPRISE_INTEGRATIONS_FETCH);
export const ENTERPRISE_INTEGRATIONS_DELETED = prefix('ENTERPRISE_INTEGRATIONS_DELETED');
export const ENTERPRISE_INTEGRATIONS_RESET = prefix('ENTERPRISE_INTEGRATIONS_RESET');

type ScopedAction<T extends string, P> = ActionWithMeta<T, P, { scope: string }>;

type FetchEnterpriseIntegrationsPayload = FetchSuccessEntitiesPayload<EnterpriseIntegration>;

export const fetchEnterpriseIntegrations =
    ({
        enterpriseId,
        integrationId,
    }: WithEnterpriseId & { integrationId?: string }): CommanderThunkAction<
        AsyncAction<FetchEnterpriseIntegrationsPayload>
    > =>
    async (dispatch, _getState, { repositories }) => {
        const promise = repositories.integrations
            .getAll({
                enterpriseId,
                integrationIds: integrationId ? [integrationId] : undefined,
            })
            .then(({ integrations }) => ({
                entities: integrations,
            }));

        return dispatch(createFetchAction(ENTERPRISE_INTEGRATIONS_FETCH, promise, { scope: enterpriseId }));
    };

export const resetEnterpriseIntegrations = ({ enterpriseId }: WithEnterpriseId) =>
    createAction(ENTERPRISE_INTEGRATIONS_RESET, undefined, { scope: enterpriseId });

export const deleteEnterpriseIntegration = ({
    integrationId,
    enterpriseId,
}: WithEnterpriseId & { integrationId: string }): ScopedAction<
    typeof ENTERPRISE_INTEGRATIONS_DELETED,
    { id: string }
> => createAction(ENTERPRISE_INTEGRATIONS_DELETED, { id: integrationId }, { scope: enterpriseId });

export const setEnterpriseIntegrations = ({
    integrations,
    enterpriseId,
}: { integrations: Array<EnterpriseIntegration> } & WithEnterpriseId): ScopedAction<
    typeof ENTERPRISE_INTEGRATIONS_FETCH_SUCCEEDED,
    FetchEnterpriseIntegrationsPayload
> => createAction(ENTERPRISE_INTEGRATIONS_FETCH_SUCCEEDED, { entities: integrations }, { scope: enterpriseId });

const dataReducer = createScopedEntitiesReducer({
    FETCH_SUCCESS: ENTERPRISE_INTEGRATIONS_FETCH_SUCCEEDED,
    DELETE_SUCCESS: ENTERPRISE_INTEGRATIONS_DELETED,
    RESET_SUCCESS: ENTERPRISE_INTEGRATIONS_RESET,
});

const metaReducer = createEntityMetaReducer({
    STARTED: ENTERPRISE_INTEGRATIONS_FETCH_STARTED,
    SUCCEEDED: ENTERPRISE_INTEGRATIONS_FETCH_SUCCEEDED,
    FAILED: ENTERPRISE_INTEGRATIONS_FETCH_FAILED,
});

export const enterpriseIntegrationsReducer = combineReducers({
    data: dataReducer,
    meta: metaReducer,
});

const getEnterpriseIntegrationState = compose<ApplicationState, EnterpriseIntegrationState, unknown>(
    get(NAME),
    parentSelector
);

const getEnterpriseIntegrationDataState = compose(get('data'), getEnterpriseIntegrationState);

export const [isEnterpriseIntegrationFetching, hasEnterpriseIntegrationFailed] =
    createAsyncMetaStateSelectors(getEnterpriseIntegrationState);

export const getEnterpriseIntegrations: Selector<
    ApplicationState,
    WithEnterpriseId,
    Entities<EnterpriseIntegration>
> = createScopedSelector({
    parentSelector: getEnterpriseIntegrationDataState,
    initialState: {},
    scopeName: 'enterpriseId',
});

export const getEnterpriseIntegration = createSelector(
    getStringProp('integrationId'),
    getEnterpriseIntegrations,
    (integrationId, entities) => entities[integrationId]
);

export const getEnterpriseIntegrationClientId = compose(get('clientId'), getEnterpriseIntegration);

export const getEnterpriseIntegrationName = compose(get('name'), getEnterpriseIntegration);

export const getEnterpriseIntegrationIsEnabled = compose(get('isEnabled'), getEnterpriseIntegration);

export const getEnterpriseIntegrationSuspensionReason = compose(get('suspensionReason'), getEnterpriseIntegration);

const getEnterpriseIntegrationInfo = compose(get('info'), getEnterpriseIntegration);

export const getEnterpriseIntegrationClassification = compose(get('classification'), getEnterpriseIntegrationInfo);

export const getEnterpriseIntegrationImageName = compose(get('imageName'), getEnterpriseIntegrationInfo);

const getEnterpriseIntegrationProductSubscriptionId = compose(
    get('enterpriseProductSubscriptionId'),
    getEnterpriseIntegration
);

export const hasEnterpriseIntegrationProductSubscriptionId = compose(
    notNil,
    getEnterpriseIntegrationProductSubscriptionId
);

const getActiveEnterpriseIntegrations = createSelector(
    getEnterpriseIntegrations,
    filter<EnterpriseIntegration>('isActive')
);

const getSortedActiveEnterpriseIntegrations = createSelector(
    getActiveEnterpriseIntegrations,
    sortBy<EnterpriseIntegration>('createdUtc')
);

export const getSortedActiveEnterpriseIntegrationIds = createSelector(getSortedActiveEnterpriseIntegrations, map('id'));

const getActiveIntegrationSubscriptions = createSelector(
    getActiveEnterpriseIntegrations,
    filter<EnterpriseIntegration>({ integrationType: EnterpriseIntegrationType.Integration })
);

export const getActiveIntegrationSubscriptionsCount = compose(size, getActiveIntegrationSubscriptions);

const createIsEnterpriseIntegrationEnabledSelector = (type: IntegrationDataType) =>
    createSelector(
        getEnterpriseIntegrations,
        (integrations) =>
            !!find<EnterpriseIntegration>({
                isActive: true,
                isEnabled: true,
                type,
            })(integrations)
    );

export const isEnterpriseAnalyticsIntegrationEnabled = createIsEnterpriseIntegrationEnabledSelector(
    IntegrationDataType.AnalyticsIntegration
);
export const isEnterpriseResDiaryIntegrationEnabled = createIsEnterpriseIntegrationEnabledSelector(
    IntegrationDataType.ResDiaryIntegration
);
export const isEnterpriseMewsReportingIntegrationEnabled = createSelector(
    getEnterpriseIntegrations,
    (integrations) =>
        !!find<EnterpriseIntegration>({
            isActive: true,
            isEnabled: true,
            name: 'Mews Reporting',
        })(integrations)
);

const getActiveSuspendedEnterpriseIntegrations = createSelector(
    getSortedActiveEnterpriseIntegrations,
    getProp<AppClockService>()('appClockService'),
    (enterpriseIntegrations, appClockService) => {
        const nowDateTimeUtc = appClockService.getAppNowUtc();

        return filter(
            ({ suspensionEndUtc, suspensionStartUtc }) =>
                (!suspensionEndUtc ||
                    DateTimeUtcService.isGreater(DateTimeUtcService.parse(suspensionEndUtc), nowDateTimeUtc)) &&
                !!suspensionStartUtc &&
                DateTimeUtcService.isGreater(nowDateTimeUtc, DateTimeUtcService.parse(suspensionStartUtc)),
            enterpriseIntegrations
        );
    }
);

export const getActiveSuspendedEnterpriseIntegrationIds = createSelector(
    getActiveSuspendedEnterpriseIntegrations,
    map('id')
);

export const isAnyClientEnterpriseIntegrationsEnabled = createSelector(
    getEnterpriseIntegrations,
    getStringProp('clientId'),
    (integrations, clientId) =>
        !!find<EnterpriseIntegration>({
            isActive: true,
            isEnabled: true,
            clientId,
        })(integrations)
);

export const hasEnterpriseSmsIntegration = createSelector(
    getEnterpriseIntegrations,
    record.some(({ type }) => type === IntegrationDataType.SmsIntegration)
);
